import { createContext, useContext, useState } from "react";

const en = {
  app_name: "Vaskion Gram Survey",
  tagline: "Village Household Information System",
  company: "Vaskion Technologies Private Limited",
  yes: "Yes", no: "No", save: "Save", cancel: "Cancel", edit: "Edit", delete: "Delete",
  view: "View", actions: "Actions", loading: "Loading…", confirm: "Confirm", search: "Search",
  all: "All", results: "Results", close: "Close", create: "Create", active: "Active",
  inactive: "Inactive", print: "Print", export_excel: "Export Excel", export_pdf: "Export PDF",
  back: "Back", total: "Total", count: "Count", na: "—",
  nav_dashboard: "Dashboard", nav_families: "Families", nav_reports: "Reports Center",
  nav_users: "User Management", nav_logout: "Logout", exit_demo: "Exit Demo",
  landing_kicker: "Village Household Information System",
  landing_title: "Every household. Every acre. Every borewell. One platform.",
  landing_sub: "Vaskion Gram Survey digitizes village household surveys — families, housing, pattas, agricultural land, crops, borewells and electricity — into a single, searchable, report-ready system of record.",
  explore_demo: "Explore Demo", login: "Login", login_with_google: "Continue with Google",
  feat_1_title: "Household Surveys",
  feat_1_desc: "Complete family profiles with members, education, profession and demographics.",
  feat_2_title: "Agriculture & Borewells",
  feat_2_desc: "Land parcels, survey numbers, crop patterns, borewell status and electricity connections.",
  feat_3_title: "Reports & Analytics",
  feat_3_desc: "Live dashboards, cross-filters, drill-downs and PDF/Excel exports.",
  feat_4_title: "Privacy & Role-Based Access",
  feat_4_desc: "Admin, Village Secretary and Demo roles with village-level authorization and audit history.",
  landing_footer: "Built for village administration. Demo data is fully synthetic.",
  welcome_back: "Welcome back", sign_in_subtitle: "Sign in to manage village household records",
  email: "Email", password: "Password", sign_in: "Sign In", signing_in: "Signing in…",
  login_failed: "Login failed", no_public_signup: "Access is provisioned by the administrator. No public signup.",
  demo_banner: "Demo Environment — Sample data only",
  demo_mode: "Demo Mode", reset_demo_data: "Reset Demo Data",
  reset_confirm_title: "Reset demo data?",
  reset_confirm_desc: "This will discard all changes made during the demo and restore the original 50 synthetic families of K. Gopalapuram.",
  reset_done: "Demo data restored to the original 50 families",
  dashboard_title: "Dashboard", dashboard_subtitle: "{village} — village information at a glance",
  kpi_total_families: "Total Families", kpi_total_population: "Total Population",
  kpi_children: "Children (0–17)", kpi_adults: "Adults (18–59)", kpi_senior_citizens: "Senior Citizens (60+)",
  kpi_own_houses: "Own Houses", kpi_without_own_house: "Families Without Own House",
  kpi_pattas_issued: "House Pattas Issued", kpi_requiring_patta: "Families Requiring House Patta",
  kpi_land_owners: "Agricultural Land Owners", kpi_landless: "Landless Families",
  kpi_total_land: "Total Agricultural Land (acres)",
  kpi_gov_borewells: "Government Borewells", kpi_private_borewells: "Private Borewells",
  kpi_bw_with_electricity: "Borewells With Electricity", kpi_bw_without_electricity: "Borewells Without Electricity",
  kpi_requiring_borewell: "Families Requiring Borewell",
  village_insights: "Village Insights", demographics_title: "Demographics",
  chart_house_ownership: "House Ownership", chart_patta_status: "House Patta Status",
  chart_land_ownership: "Agricultural Land Ownership", chart_crop_distribution: "Crop Distribution (farmers)",
  chart_borewell_status: "Borewell Status", chart_borewell_electricity: "Borewell Electricity",
  chart_education: "Education Distribution", chart_profession: "Profession Distribution",
  chart_caste: "Caste / Category Distribution", chart_gender: "Gender",
  chart_age_groups: "Age Groups", chart_marital: "Marital Status",
  chart_season: "Seasonal Crop Distribution", chart_wards: "Families by Ward",
  insight_requiring_patta: "{count} families require a house patta",
  insight_bw_requiring_electricity: "{count} borewells require an electricity connection",
  insight_top_crop: "{crop} is the most cultivated crop ({count} farming families)",
  insight_landless: "{count} families do not own agricultural land",
  insight_requiring_borewell: "{count} families require a new borewell",
  insight_seniors: "{count} senior citizens live in the village",
  caste_disclaimer: "Caste/category values in the demo environment are synthetic and are used only to demonstrate application functionality.",
  families_title: "Families", families_subtitle: "All household records in scope",
  add_family: "Add Family", search_placeholder: "Search family ID, name, street…",
  filters: "Filters", clear_filters: "Clear filters",
  filter_ward: "Ward", filter_caste: "Caste / Category", filter_own_house: "Own House",
  filter_patta: "Patta Issued", filter_needs_patta: "Needs House Patta", filter_owns_land: "Owns Agri Land",
  filter_has_borewell: "Has Borewell", filter_bw_type: "Borewell Type", filter_bw_status: "Borewell Status",
  filter_bw_electricity: "BW Electricity", filter_needs_borewell: "Needs Borewell",
  filter_crop: "Crop", filter_season: "Season", filter_irrigation: "Irrigation Source",
  filter_education: "Education", filter_profession: "Profession", filter_gender: "Gender",
  filter_marital: "Marital Status", filter_age_group: "Age Group",
  age_child: "Children (0–17)", age_adult: "Adults (18–59)", age_senior: "Seniors (60+)",
  col_family_id: "Family ID", col_head: "Head of Family", col_ward: "Ward", col_members: "Members",
  col_caste: "Category", col_own_house: "Own House", col_land: "Land (ac)", col_borewells: "Borewells", col_crops: "Crops",
  no_families: "No families match the current filters",
  delete_confirm_title: "Delete this family?",
  delete_confirm_desc: "The family record will be archived and removed from all reports.",
  deleted: "Family deleted",
  basic_details: "Basic Details", housing_details: "Housing", agriculture_details: "Agricultural Land",
  family_members: "Family Members", add_member: "Add Member",
  land_parcels: "Land Parcels", add_parcel: "Add Parcel",
  crops_details: "Crops", add_crop: "Add Crop",
  borewells_details: "Borewells & Electricity", add_borewell: "Add Borewell",
  f_family_id: "Family ID", f_village: "Village", f_ward: "Ward", f_street: "Street", f_address: "Address",
  f_own_house: "Owns a house?", f_house_type: "House Type", f_patta_issued: "House patta issued?",
  f_patta_number: "Patta Number", f_needs_patta: "Requires new house patta?", f_owns_land: "Owns agricultural land?",
  m_name: "Full Name", m_relation: "Relation", m_gender: "Gender", m_dob: "Date of Birth", m_age: "Age",
  m_marital: "Marital Status", m_education: "Education", m_profession: "Profession", m_caste: "Caste / Category",
  p_survey: "Survey Number", p_acres: "Acres", p_cents: "Cents", p_land_type: "Land Type",
  c_crop: "Crop", c_season: "Season", c_year: "Year", c_irrigation: "Irrigation Source",
  b_type: "Borewell Type", b_status: "Status", b_depth: "Depth (ft)",
  b_electricity: "Electricity connection?", b_service_no: "Service / Connection No.", b_conn_date: "Connection Date",
  f_needs_borewell: "Requires a new borewell?",
  add_family_title: "Add Family Survey", edit_family_title: "Edit Family Survey",
  saved: "Saved successfully", save_failed: "Save failed",
  validation_members: "Add at least one member with name, gender and date of birth",
  validation_head: "Exactly one member must be the Head of Family (relation: Self)",
  total_land: "Total Land", head_label: "Head",
  reports_title: "Reports Center", reports_subtitle: "Authorized aggregated and drill-down reports",
  select_report: "Select a report to view matching records",
  families_count: "Families", people: "People", percentage: "% of Population",
  category: "Category", education_level: "Education Level", profession_label: "Profession",
  group_housing: "Housing", group_agriculture: "Agriculture", group_crops: "Crops",
  group_borewell: "Borewell", group_electricity: "Electricity", group_demographics: "Demographics",
  group_education: "Education", group_profession: "Profession", group_caste: "Caste / Category",
  rep_own_house: "Families With Own House", rep_without_own_house: "Families Without Own House",
  rep_pattas_issued: "House Pattas Issued", rep_requiring_patta: "Families Requiring House Patta",
  rep_land_owners: "Land-owning Families", rep_landless: "Landless Families",
  rep_crop_farmers: "Crop-wise Farmers", rep_seasonal: "Seasonal Crop Distribution",
  rep_gov_bw: "Government Borewells", rep_pvt_bw: "Private Borewells",
  rep_working_bw: "Working Borewells", rep_nonworking_bw: "Non-working Borewells",
  rep_dry_bw: "Dry Borewells", rep_repair_bw: "Borewells — Repair Required",
  rep_requiring_bw: "Families Requiring Borewell",
  rep_bw_with_elec: "Borewells With Electricity", rep_bw_without_elec: "Borewells Without Electricity",
  rep_bw_req_elec: "Borewells Requiring Electricity Connection",
  rep_gender: "Gender Distribution", rep_age_groups: "Age Group Distribution", rep_marital: "Marital Status Distribution",
  rep_education_dist: "Education-level Distribution", rep_profession_dist: "Profession Distribution",
  rep_caste_dist: "Caste / Category Distribution",
  users_title: "User Management", users_subtitle: "Provision access for administrators, village secretaries and demo users",
  create_user: "Create User", u_name: "Name", u_email: "Email", u_role: "Role",
  u_village: "Assigned Village", u_password: "Password",
  role_admin: "Admin", role_secretary: "Village Secretary", role_demo: "Demo User",
  user_created: "User created", user_deleted: "User deleted", status_label: "Status",
  scope_production: "Production", scope_demo_data: "Demo Data",
  empty_dashboard: "No records in scope yet. Add a family to get started.",
};

const te = {
  app_name: "Vaskion Gram Survey",
  tagline: "గ్రామ గృహ సమాచార వ్యవస్థ",
  company: "Vaskion Technologies Private Limited",
  yes: "అవును", no: "కాదు", save: "సేవ్ చేయండి", cancel: "రద్దు", edit: "సవరించండి",
  delete: "తొలగించండి", view: "చూడండి", actions: "చర్యలు", loading: "లోడ్ అవుతోంది…",
  confirm: "నిర్ధారించండి", search: "వెతకండి", all: "అన్నీ", results: "ఫలితాలు",
  close: "మూసివేయండి", create: "సృష్టించండి", active: "క్రియాశీలం", inactive: "నిష్క్రియం",
  print: "ప్రింట్", export_excel: "Excel ఎగుమతి", export_pdf: "PDF ఎగుమతి",
  back: "వెనుకకు", total: "మొత్తం", count: "సంఖ్య", na: "—",
  nav_dashboard: "డాష్‌బోర్డ్", nav_families: "కుటుంబాలు", nav_reports: "నివేదికల కేంద్రం",
  nav_users: "వినియోగదారుల నిర్వహణ", nav_logout: "లాగ్ అవుట్", exit_demo: "డెమో నుండి నిష్క్రమించండి",
  landing_kicker: "గ్రామ గృహ సమాచార వ్యవస్థ",
  landing_title: "ప్రతి ఇల్లు. ప్రతి ఎకరం. ప్రతి బోరుబావి. ఒకే వేదిక.",
  landing_sub: "Vaskion Gram Survey గ్రామ గృహ సర్వేలను — కుటుంబాలు, గృహనిర్మాణం, పట్టాలు, వ్యవసాయ భూమి, పంటలు, బోరుబావులు మరియు విద్యుత్ — ఒకే అన్వేషణయోగ్యమైన, నివేదిక-సిద్ధ వ్యవస్థలోకి డిజిటైజ్ చేస్తుంది.",
  explore_demo: "డెమో అన్వేషించండి", login: "లాగిన్", login_with_google: "Google తో కొనసాగండి",
  feat_1_title: "గృహ సర్వేలు",
  feat_1_desc: "సభ్యులు, విద్య, వృత్తి మరియు జనాభా వివరాలతో పూర్తి కుటుంబ ప్రొఫైళ్లు.",
  feat_2_title: "వ్యవసాయం & బోరుబావులు",
  feat_2_desc: "భూమి పార్సెళ్లు, సర్వే నంబర్లు, పంటలు, బోరుబావి స్థితి మరియు విద్యుత్ కనెక్షన్లు.",
  feat_3_title: "నివేదికలు & విశ్లేషణలు",
  feat_3_desc: "ప్రత్యక్ష డాష్‌బోర్డులు, క్రాస్-ఫిల్టర్లు, డ్రిల్-డౌన్లు మరియు PDF/Excel ఎగుమతులు.",
  feat_4_title: "గోప్యత & పాత్ర-ఆధారిత యాక్సెస్",
  feat_4_desc: "గ్రామ-స్థాయి అధికారం మరియు ఆడిట్ చరిత్రతో అడ్మిన్, విలేజ్ సెక్రటరీ మరియు డెమో పాత్రలు.",
  landing_footer: "గ్రామ పరిపాలన కోసం నిర్మించబడింది. డెమో డేటా పూర్తిగా కృత్రిమమైనది.",
  welcome_back: "తిరిగి స్వాగతం", sign_in_subtitle: "గ్రామ గృహ రికార్డులను నిర్వహించడానికి సైన్ ఇన్ చేయండి",
  email: "ఇమెయిల్", password: "పాస్‌వర్డ్", sign_in: "సైన్ ఇన్", signing_in: "సైన్ ఇన్ అవుతోంది…",
  login_failed: "లాగిన్ విఫలమైంది", no_public_signup: "యాక్సెస్ నిర్వాహకుడు అందిస్తారు. పబ్లిక్ సైనప్ లేదు.",
  demo_banner: "డెమో వాతావరణం — నమూనా డేటా మాత్రమే",
  demo_mode: "డెమో మోడ్", reset_demo_data: "డెమో డేటా రీసెట్",
  reset_confirm_title: "డెమో డేటాను రీసెట్ చేయాలా?",
  reset_confirm_desc: "డెమోలో చేసిన అన్ని మార్పులు తొలగిపోతాయి మరియు K. Gopalapuram యొక్క అసలు 50 కృత్రిమ కుటుంబాలు పునరుద్ధరించబడతాయి.",
  reset_done: "డెమో డేటా అసలు 50 కుటుంబాలకు పునరుద్ధరించబడింది",
  dashboard_title: "డాష్‌బోర్డ్", dashboard_subtitle: "{village} — గ్రామ సమాచారం ఒక్క చూపులో",
  kpi_total_families: "మొత్తం కుటుంబాలు", kpi_total_population: "మొత్తం జనాభా",
  kpi_children: "పిల్లలు (0–17)", kpi_adults: "పెద్దలు (18–59)", kpi_senior_citizens: "వృద్ధులు (60+)",
  kpi_own_houses: "స్వంత ఇళ్లు", kpi_without_own_house: "స్వంత ఇల్లు లేని కుటుంబాలు",
  kpi_pattas_issued: "జారీ చేసిన ఇల్లు పట్టాలు", kpi_requiring_patta: "ఇల్లు పట్టా అవసరమైన కుటుంబాలు",
  kpi_land_owners: "వ్యవసాయ భూమి యజమానులు", kpi_landless: "భూమి లేని కుటుంబాలు",
  kpi_total_land: "మొత్తం వ్యవసాయ భూమి (ఎకరాలు)",
  kpi_gov_borewells: "ప్రభుత్వ బోరుబావులు", kpi_private_borewells: "ప్రైవేట్ బోరుబావులు",
  kpi_bw_with_electricity: "విద్యుత్ కల బోరుబావులు", kpi_bw_without_electricity: "విద్యుత్ లేని బోరుబావులు",
  kpi_requiring_borewell: "బోరుబావి అవసరమైన కుటుంబాలు",
  village_insights: "గ్రామ అంతర్దృష్టులు", demographics_title: "జనాభా వివరాలు",
  chart_house_ownership: "గృహ యాజమాన్యం", chart_patta_status: "ఇల్లు పట్టా స్థితి",
  chart_land_ownership: "వ్యవసాయ భూమి యాజమాన్యం", chart_crop_distribution: "పంటల పంపిణీ (రైతులు)",
  chart_borewell_status: "బోరుబావి స్థితి", chart_borewell_electricity: "బోరుబావి విద్యుత్",
  chart_education: "విద్యా స్థాయి పంపిణీ", chart_profession: "వృత్తుల పంపిణీ",
  chart_caste: "కులం / వర్గం పంపిణీ", chart_gender: "లింగం",
  chart_age_groups: "వయస్సు సమూహాలు", chart_marital: "వైవాహిక స్థితి",
  chart_season: "సీజన్‌వారీ పంటలు", chart_wards: "వార్డువారీ కుటుంబాలు",
  insight_requiring_patta: "{count} కుటుంబాలకు ఇల్లు పట్టా అవసరం",
  insight_bw_requiring_electricity: "{count} బోరుబావులకు విద్యుత్ కనెక్షన్ అవసరం",
  insight_top_crop: "{crop} అత్యంత అధికంగా సాగు చేసే పంట ({count} రైతు కుటుంబాలు)",
  insight_landless: "{count} కుటుంబాలకు వ్యవసాయ భూమి లేదు",
  insight_requiring_borewell: "{count} కుటుంబాలకు కొత్త బోరుబావి అవసరం",
  insight_seniors: "గ్రామంలో {count} మంది వృద్ధులు ఉన్నారు",
  caste_disclaimer: "డెమో వాతావరణంలోని కులం/వర్గ విలువలు కృత్రిమమైనవి మరియు అప్లికేషన్ ఫంక్షనాలిటీని ప్రదర్శించడానికి మాత్రమే ఉపయోగించబడతాయి.",
  families_title: "కుటుంబాలు", families_subtitle: "పరిధిలోని అన్ని గృహ రికార్డులు",
  add_family: "కుటుంబాన్ని జోడించండి", search_placeholder: "కుటుంబ ID, పేరు, వీధి వెతకండి…",
  filters: "వడపోతలు", clear_filters: "వడపోతలు తుడిచివేయండి",
  filter_ward: "వార్డు", filter_caste: "కులం / వర్గం", filter_own_house: "స్వంత ఇల్లు",
  filter_patta: "పట్టా జారీ", filter_needs_patta: "ఇల్లు పట్టా అవసరం", filter_owns_land: "వ్యవసాయ భూమి",
  filter_has_borewell: "బోరుబావి ఉంది", filter_bw_type: "బోరుబావి రకం", filter_bw_status: "బోరుబావి స్థితి",
  filter_bw_electricity: "బోరుబావి విద్యుత్", filter_needs_borewell: "బోరుబావి అవసరం",
  filter_crop: "పంట", filter_season: "సీజన్", filter_irrigation: "నీటి వనరు",
  filter_education: "విద్య", filter_profession: "వృత్తి", filter_gender: "లింగం",
  filter_marital: "వైవాహిక స్థితి", filter_age_group: "వయస్సు సమూహం",
  age_child: "పిల్లలు (0–17)", age_adult: "పెద్దలు (18–59)", age_senior: "వృద్ధులు (60+)",
  col_family_id: "కుటుంబ ID", col_head: "కుటుంబ అధినేత", col_ward: "వార్డు", col_members: "సభ్యులు",
  col_caste: "వర్గం", col_own_house: "స్వంత ఇల్లు", col_land: "భూమి (ఎక.)", col_borewells: "బోరుబావులు", col_crops: "పంటలు",
  no_families: "ప్రస్తుత వడపోతలకు సరిపోలే కుటుంబాలు లేవు",
  delete_confirm_title: "ఈ కుటుంబాన్ని తొలగించాలా?",
  delete_confirm_desc: "కుటుంబ రికార్డు ఆర్కైవ్ చేయబడి అన్ని నివేదికల నుండి తొలగించబడుతుంది.",
  deleted: "కుటుంబం తొలగించబడింది",
  basic_details: "ప్రాథమిక వివరాలు", housing_details: "గృహనిర్మాణం", agriculture_details: "వ్యవసాయ భూమి",
  family_members: "కుటుంబ సభ్యులు", add_member: "సభ్యుడిని జోడించండి",
  land_parcels: "భూమి పార్సెళ్లు", add_parcel: "పార్సెల్ జోడించండి",
  crops_details: "పంటలు", add_crop: "పంట జోడించండి",
  borewells_details: "బోరుబావులు & విద్యుత్", add_borewell: "బోరుబావి జోడించండి",
  f_family_id: "కుటుంబ ID", f_village: "గ్రామం", f_ward: "వార్డు", f_street: "వీధి", f_address: "చిరునామా",
  f_own_house: "స్వంత ఇల్లు ఉందా?", f_house_type: "ఇంటి రకం", f_patta_issued: "ఇల్లు పట్టా జారీ అయిందా?",
  f_patta_number: "పట్టా సంఖ్య", f_needs_patta: "కొత్త ఇల్లు పట్టా అవసరమా?", f_owns_land: "వ్యవసాయ భూమి ఉందా?",
  m_name: "పూర్తి పేరు", m_relation: "సంబంధం", m_gender: "లింగం", m_dob: "పుట్టిన తేదీ", m_age: "వయస్సు",
  m_marital: "వైవాహిక స్థితి", m_education: "విద్య", m_profession: "వృత్తి", m_caste: "కులం / వర్గం",
  p_survey: "సర్వే నంబర్", p_acres: "ఎకరాలు", p_cents: "సెంట్లు", p_land_type: "భూమి రకం",
  c_crop: "పంట", c_season: "సీజన్", c_year: "సంవత్సరం", c_irrigation: "నీటి వనరు",
  b_type: "బోరుబావి రకం", b_status: "స్థితి", b_depth: "లోతు (అడుగులు)",
  b_electricity: "విద్యుత్ కనెక్షన్ ఉందా?", b_service_no: "సర్వీస్ / కనెక్షన్ నం.", b_conn_date: "కనెక్షన్ తేదీ",
  f_needs_borewell: "కొత్త బోరుబావి అవసరమా?",
  add_family_title: "కుటుంబ సర్వే జోడించండి", edit_family_title: "కుటుంబ సర్వే సవరించండి",
  saved: "విజయవంతంగా సేవ్ చేయబడింది", save_failed: "సేవ్ విఫలమైంది",
  validation_members: "పేరు, లింగం మరియు పుట్టిన తేదీతో కనీసం ఒక్క సభ్యుడిని జోడించండి",
  validation_head: "ఖచ్చితంగా ఒక్క సభ్యుడు కుటుంబ అధినేత (సంబంధం: Self) అయి ఉండాలి",
  total_land: "మొత్తం భూమి", head_label: "అధినేత",
  reports_title: "నివేదికల కేంద్రం", reports_subtitle: "అధీకృత సమిష్టి మరియు డ్రిల్-డౌన్ నివేదికలు",
  select_report: "సరిపోలే రికార్డులను చూడటానికి నివేదికను ఎంచుకోండి",
  families_count: "కుటుంబాలు", people: "వ్యక్తులు", percentage: "జనాభా శాతం",
  category: "వర్గం", education_level: "విద్యా స్థాయి", profession_label: "వృత్తి",
  group_housing: "గృహనిర్మాణం", group_agriculture: "వ్యవసాయం", group_crops: "పంటలు",
  group_borewell: "బోరుబావులు", group_electricity: "విద్యుత్", group_demographics: "జనాభా",
  group_education: "విద్య", group_profession: "వృత్తి", group_caste: "కులం / వర్గం",
  rep_own_house: "స్వంత ఇల్లు ఉన్న కుటుంబాలు", rep_without_own_house: "స్వంత ఇల్లు లేని కుటుంబాలు",
  rep_pattas_issued: "జారీ చేసిన ఇల్లు పట్టాలు", rep_requiring_patta: "ఇల్లు పట్టా అవసరమైన కుటుంబాలు",
  rep_land_owners: "భూమి ఉన్న కుటుంబాలు", rep_landless: "భూమి లేని కుటుంబాలు",
  rep_crop_farmers: "పంట-వారీ రైతులు", rep_seasonal: "సీజన్‌వారీ పంటల పంపిణీ",
  rep_gov_bw: "ప్రభుత్వ బోరుబావులు", rep_pvt_bw: "ప్రైవేట్ బోరుబావులు",
  rep_working_bw: "పనిచేసే బోరుబావులు", rep_nonworking_bw: "పనిచేయని బోరుబావులు",
  rep_dry_bw: "ఎండిపోయిన బోరుబావులు", rep_repair_bw: "బోరుబావులు — మరమ్మతు అవసరం",
  rep_requiring_bw: "బోరుబావి అవసరమైన కుటుంబాలు",
  rep_bw_with_elec: "విద్యుత్ కల బోరుబావులు", rep_bw_without_elec: "విద్యుత్ లేని బోరుబావులు",
  rep_bw_req_elec: "విద్యుత్ కనెక్షన్ అవసరమైన బోరుబావులు",
  rep_gender: "లింగ పంపిణీ", rep_age_groups: "వయస్సు సమూహాల పంపిణీ", rep_marital: "వైవాహిక స్థితి పంపిణీ",
  rep_education_dist: "విద్యా స్థాయి పంపిణీ", rep_profession_dist: "వృత్తుల పంపిణీ",
  rep_caste_dist: "కులం / వర్గం పంపిణీ",
  users_title: "వినియోగదారుల నిర్వహణ", users_subtitle: "అడ్మిన్లు, విలేజ్ సెక్రటరీలు మరియు డెమో వినియోగదారులకు యాక్సెస్ ఇవ్వండి",
  create_user: "వినియోగదారుని సృష్టించండి", u_name: "పేరు", u_email: "ఇమెయిల్", u_role: "పాత్ర",
  u_village: "కేటాయించిన గ్రామం", u_password: "పాస్‌వర్డ్",
  role_admin: "అడ్మిన్", role_secretary: "విలేజ్ సెక్రటరీ", role_demo: "డెమో వినియోగదారు",
  user_created: "వినియోగదారు సృష్టించబడ్డారు", user_deleted: "వినియోగదారు తొలగించబడ్డారు", status_label: "స్థితి",
  scope_production: "ప్రొడక్షన్", scope_demo_data: "డెమో డేటా",
  empty_dashboard: "పరిధిలో ఇంకా రికార్డులు లేవు. ప్రారంభించడానికి కుటుంబాన్ని జోడించండి.",
};

const dictionaries = { en, te };
const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem("vgs_lang") || "en");
  const setLang = (l) => {
    localStorage.setItem("vgs_lang", l);
    setLangState(l);
  };
  const t = (key, params) => {
    let s = dictionaries[lang]?.[key] ?? dictionaries.en[key] ?? key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        s = s.replace(`{${k}}`, v);
      });
    }
    return s;
  };
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
